import { defineStore } from 'pinia';

import { MANUSCRIPT_V1_API_URL_PREFIX as URL_PREFIX } from '@/lib/constants/manuscripts/api';
import * as action from '@/lib/constants/manuscripts/store';
import {
  DOMAINS,
  FIVE_E_CATEGORIES,
  GRADE_LEVELS,
  LANGUAGE,
  LESSON_TYPES,
  PAGE_LABELS,
  READING_LEVELS,
} from '@/lib/constants/manuscripts/metadataForm';

/**
 * Private helper function - maps API data to dropdown options with fallback behavior
 * @param {Array} apiData - Array of objects from API response
 * @param {string} dropdownLabelKey - Key on the object for option's label
 * @param {string} fieldName - Field name for console warning message
 * @returns {Array|undefined} Mapped options array in format [{label, value}], or undefined if data missing
 */
function mapToDropdownOptions(apiData, dropdownLabelKey, fieldName) {
  if (apiData && apiData.length > 0) {
    return apiData.map((item) => ({
      label: item[dropdownLabelKey],
      value: item.id,
    }));
  }

  window.console.warn(
    `Product Config API: ${fieldName} field missing or empty, using static fallback`,
  );

  // Return undefined to keep existing state value (static constant)
  return undefined;
}

const useProductConfigStore = defineStore(
  'manuscript-productConfig',

  {
    state: () => ({
      config: null,
      isLoading: false,
      // Metadata form options (defaults from constants, will be overridden by API when available)
      domainOptions: DOMAINS,
      fiveECategoryOptions: FIVE_E_CATEGORIES,
      gradeLevelOptions: GRADE_LEVELS,
      languageOptions: LANGUAGE,
      lessonTypeOptions: LESSON_TYPES,
      pageLabelOptions: PAGE_LABELS,
      readingLevelOptions: READING_LEVELS,
    }),

    getters: {
      // TODO: WIP
    },

    actions: {
      async [action.GET_PRODUCT_CONFIG](productId) {
        // Check cache first - compare by product_id if config exists
        if (this.config?.product_id === productId) {
          window.console.log('📋 Product Config: Using cached data for', productId);
          return this.config;
        }

        this.isLoading = true;

        try {
          const response = await this.appApi(`${URL_PREFIX}/product_config/${productId}`);
          this.config = response.data || null;

          // Update options from API when available
          if (this.config) {
            // Map API response fields to dropdown options
            // Note: grades use 'grade' key for dropdownLabelKey instead of 'title' per API schema
            const mappedDomains = mapToDropdownOptions(this.config.domains, 'title', 'domains');
            const mappedTypes = mapToDropdownOptions(this.config.types, 'title', 'types');
            const mappedGrades = mapToDropdownOptions(this.config.grades, 'grade', 'grades');
            const mappedTags = mapToDropdownOptions(this.config.tags, 'title', 'tags');
            const mappedCategories = mapToDropdownOptions(this.config.categories, 'title', 'categories');

            // Only update if API returned data (undefined means fallback to static constants)
            if (mappedDomains) this.domainOptions = mappedDomains;
            if (mappedTypes) this.lessonTypeOptions = mappedTypes;
            if (mappedGrades) this.gradeLevelOptions = mappedGrades;
            if (mappedTags) this.pageLabelOptions = mappedTags;
            if (mappedCategories) this.fiveECategoryOptions = mappedCategories;

            // Note: Languages and Reading Levels are not yet exposed in the API schema
            // These will continue to use static constants until backend updates are complete (Phase 2)
          }

          return this.config;
        } catch (error) {
          window.console.error('🏪 Product Config Store: API call failed', error);
          return null;
        } finally {
          this.isLoading = false;
        }
      },
    },
  },
);

export default useProductConfigStore;
